"use client";

import { env } from "@/env";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { toast } from "@/app/_providers/toast";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/nextjs";
import { getCaptchaToken } from "@/app/_providers/hcaptcha/core";
import { handleMicrophoneError } from "@utilities/errors";
import { logger } from "@utilities/logger";
import { useOmniboxStateOptional } from "../OmniboxStateContext";

// Constants
const SAMPLE_RATE = 16000;
const TARGET_CHUNK_SIZE = 4096; // ~256ms of audio at 16kHz
const KEEPALIVE_INTERVAL = 30000; // 30 seconds

// Types
interface WebSocketMessage {
  message_type: string;
  text?: string;
  is_final?: boolean;
  error?: string;
  error_message?: string;
  error_code?: string;
  language_code?: string;
  logprob?: number;
  words?: unknown[];
  [key: string]: unknown;
}

export type RecordingStatus =
  | "idle"
  | "requesting_permission"
  | "setting_up_audio"
  | "connecting"
  | "recording"
  | "finished"
  | "session_limit_reached"
  | "quota_exceeded";

function buildWebSocketUrl(hcaptcha_token?: null | string): string {
  const serverUrl = env.NEXT_PUBLIC_XI_SERVER_URL;
  const basePath = "/v1/speech-to-text/realtime/anonymous";
  const url = new URL(`${serverUrl}${basePath}`);

  url.searchParams.append("model_id", "scribe_v2_realtime");
  url.searchParams.append("audio_format", "pcm_16000");
  if (hcaptcha_token) {
    url.searchParams.append("hcaptcha_token", hcaptcha_token);
  }

  return url.toString();
}

export function useSTTRealtime() {
  const t = useTranslations("ClientKeys.SpeechToText.realtime");
  const tMicrophoneErrors = useTranslations("ClientKeys.Errors.Microphone");
  const omniboxState = useOmniboxStateOptional();

  // State
  const [status, setStatus] = useState<RecordingStatus>("idle");
  const [committedTranscript, setCommittedTranscript] = useState("");
  const [partialTranscript, setPartialTranscript] = useState("");

  // Audio refs
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);

  // WebSocket refs
  const socketRef = useRef<WebSocket | null>(null);
  const keepaliveIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Data refs
  const audioBufferRef = useRef<Int16Array[]>([]);
  const sessionStartTimeRef = useRef<number | null>(null);

  // Derived state
  const isRecording = status === "recording";
  const isSettingUp = ["requesting_permission", "setting_up_audio", "connecting"].includes(status);

  // WebSocket Functions
  const sendAudioChunk = (base64Audio: string) => {
    if (socketRef.current?.readyState !== WebSocket.OPEN) return;

    const message = {
      message_type: "input_audio_chunk",
      audio_base_64: base64Audio,
      commit: false,
      sample_rate: SAMPLE_RATE,
    };

    socketRef.current.send(JSON.stringify(message));
  };

  const handleWebSocketMessage = (message: WebSocketMessage) => {
    switch (message.message_type) {
      case "session_started":
        setStatus("recording");
        omniboxState?.setState("active");
        sessionStartTimeRef.current = Date.now();
        captureEvent(EventName.mw_stt_realtime_started);
        break;

      case "transcript":
      case "partial_transcript":
      case "final_transcript": {
        const isFinal = message.message_type === "final_transcript" || message.is_final === true;
        const text = message.text || "";

        if (isFinal && text && text.trim()) {
          setCommittedTranscript(prev => prev + (prev ? " " : "") + text);
          setPartialTranscript("");
        } else if (!isFinal && text) {
          setPartialTranscript(text);
        }
        break;
      }

      case "session_time_limit_exceeded":
        stopRecording();
        setStatus("session_limit_reached");
        captureEvent(EventName.mw_stt_realtime_session_limit_reached);
        break;

      case "quota_exceeded":
        stopRecording();
        setStatus("quota_exceeded");
        captureEvent(EventName.mw_stt_realtime_quota_exceeded);
        break;

      case "error":
        const errorMessage = message.error_message || message.error || t("errors.unknown_error");
        toast.error(t("errors.transcription_error"), {
          description: errorMessage,
        });

        // Track error in PostHog
        captureEvent(EventName.mw_stt_realtime_error, {
          error_code: message.error_code,
          error_message: errorMessage,
        });

        // Log error to Sentry
        Sentry.captureException(new Error(`Live transcription error: ${errorMessage}`), {
          extra: {
            error_code: message.error_code,
            error_message: errorMessage,
            message_type: message.message_type,
          },
        });
        break;

      default:
        break;
    }
  };

  const connectWebSocket = (hcaptcha_token?: null | string): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        const wsUrl = buildWebSocketUrl(hcaptcha_token);
        const ws = new WebSocket(wsUrl);
        socketRef.current = ws;

        ws.onopen = () => {
          keepaliveIntervalRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              sendAudioChunk("");
            }
          }, KEEPALIVE_INTERVAL);

          resolve();
        };

        ws.onmessage = event => {
          try {
            const message = JSON.parse(event.data) as WebSocketMessage;
            handleWebSocketMessage(message);
          } catch {
            // Silently ignore parse errors
            logger.error(null, "Error parsing WebSocket message", event.data);
            Sentry.captureException(new Error("Error parsing WebSocket message"), {
              extra: {
                eventData: event.data,
              },
            });
          }
        };

        ws.onerror = () => {
          reject(new Error("WebSocket connection error"));
        };

        ws.onclose = () => {
          if (keepaliveIntervalRef.current) {
            clearInterval(keepaliveIntervalRef.current);
            keepaliveIntervalRef.current = null;
          }
        };
      } catch (error) {
        reject(error);
      }
    });
  };

  const closeWebSocket = () => {
    if (socketRef.current) {
      const ws = socketRef.current;

      // If WebSocket is already open, close it immediately
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
      // If WebSocket is still connecting, wait for it to open then close
      else if (ws.readyState === WebSocket.CONNECTING) {
        ws.addEventListener("open", () => {
          ws.close();
        });
      }

      socketRef.current = null;
    }

    if (keepaliveIntervalRef.current) {
      clearInterval(keepaliveIntervalRef.current);
      keepaliveIntervalRef.current = null;
    }
  };

  // Audio Processing Functions
  const processAudioChunk = (pcmData: Int16Array) => {
    audioBufferRef.current.push(pcmData);

    const totalSamples = audioBufferRef.current.reduce((sum, chunk) => sum + chunk.length, 0);

    if (totalSamples >= TARGET_CHUNK_SIZE) {
      // Concatenate accumulated chunks
      const concatenated = new Int16Array(totalSamples);
      let offset = 0;
      for (const chunk of audioBufferRef.current) {
        concatenated.set(chunk, offset);
        offset += chunk.length;
      }

      // Extract exactly TARGET_CHUNK_SIZE samples
      const toSend = concatenated.slice(0, TARGET_CHUNK_SIZE);
      const remainder = concatenated.slice(TARGET_CHUNK_SIZE);

      // Convert to base64 and send
      const uint8Array = new Uint8Array(toSend.buffer);
      const base64 = btoa(String.fromCharCode(...uint8Array));
      sendAudioChunk(base64);

      // Update buffer
      audioBufferRef.current = remainder.length > 0 ? [remainder] : [];
    }
  };

  const setupAudioWorklet = async (stream: MediaStream, audioContext: AudioContext) => {
    const versionCacheBust = "1";
    await audioContext.audioWorklet.addModule(`/audio-processor.js?v=${versionCacheBust}`);

    const source = audioContext.createMediaStreamSource(stream);
    const workletNode = new AudioWorkletNode(audioContext, "audio-capture-processor");

    workletNode.port.onmessage = event => {
      if (event.data.type === "audio") {
        const pcmData = new Int16Array(event.data.data);
        processAudioChunk(pcmData);
      }
    };

    // Connect audio graph with muted gain to avoid feedback
    const gainNode = audioContext.createGain();
    gainNode.gain.value = 0;

    source.connect(workletNode);
    workletNode.connect(gainNode);
    gainNode.connect(audioContext.destination);

    sourceRef.current = source;
    workletNodeRef.current = workletNode;
  };

  const cleanupAudioResources = () => {
    if (workletNodeRef.current) {
      workletNodeRef.current.port.onmessage = null;
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }

    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }

    audioBufferRef.current = [];
  };

  // Recording Control Functions
  const startRecording = async () => {
    if (isRecording || isSettingUp) {
      return;
    }

    try {
      // Start setup process
      setStatus("requesting_permission");
      setCommittedTranscript("");
      setPartialTranscript("");

      // Get microphone stream first
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: SAMPLE_RATE,
          channelCount: 1,
          echoCancellation: false,
          noiseSuppression: false,
        },
      });
      streamRef.current = stream;
      setStatus("setting_up_audio");

      // Setup audio processing
      const audioContext = new AudioContext({ sampleRate: SAMPLE_RATE });
      audioContextRef.current = audioContext;
      await setupAudioWorklet(stream, audioContext);
      setStatus("connecting");

      // Connect WebSocket after audio is ready
      const token = await getCaptchaToken({ behaviourType: "stt" });
      await connectWebSocket(token);
    } catch (error) {
      closeWebSocket();
      cleanupAudioResources();
      setStatus("idle");

      if (error instanceof Error) {
        if (
          handleMicrophoneError(error, {
            handler: (title, description) => toast.error(title, { description }),
            t: tMicrophoneErrors,
            demoType: "stt_realtime",
          })
        ) {
          return;
        } else {
          toast.error(t("errors.failed_to_start"), { description: error.message });
        }
      }
    }
  };

  const stopRecording = () => {
    // Track session end with duration
    if (sessionStartTimeRef.current !== null) {
      const durationMs = Date.now() - sessionStartTimeRef.current;
      const durationSeconds = Math.round(durationMs / 1000);

      captureEvent(EventName.mw_stt_realtime_session_ended, {
        duration_seconds: durationSeconds,
      });

      sessionStartTimeRef.current = null;
    }

    omniboxState?.setState("idle");
    closeWebSocket();
    cleanupAudioResources();
    setStatus("idle");
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecording();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    status,
    setStatus,
    committedTranscript,
    setCommittedTranscript,
    partialTranscript,
    setPartialTranscript,
    startRecording,
    stopRecording,
  };
}
